import '../../../styles/pages.css'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import AddCurriculum from '../shared/AddCurriculum'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { GetSchoolCurricula } from '../shared/GetCurricula'
import { SchoolFeatures } from './SchoolFeatures'
import SchoolHeader from './SchoolHeader'
import SyncSISButton from '../shared/SyncSISButton'
import { UserCreateWithSchool } from '../user_components/UserCreate'
import { apiUrl } from 'infrastructure/api'
import { getAPIRequest } from 'infrastructure/api'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { schoolUserCountsWithDefaultAtom } from '../../../api/school.atoms'
import { useSchoolResource } from '../../../api/school.hooks'
import ArchiveClassButton from '../shared/ArchiveClassButton'
import { PlainLink } from 'app/components/RouteLink'
import ReinstateResourceButton from '../shared/ReinstateResourceButton'
import DeleteOrgUsersButton from '../shared/DeleteOrgUsersButton'
import DeleteDuplicateCurriculaButton from '../shared/DeleteDuplicateCurriculaButton'
import { CSVUpload } from 'shared/ui'

export function SchoolShow({ id }: { id: number | string }) {
  const {
    updateSchoolSearchable: updateSearchable,
    data,
    reFetchSchool,
  } = useSchoolResource(id)
  const [currentDialog] = useState<ReactElement | null>()
  const [hasSAML, setHasSAML] = useState(false)
  const archiveButtonLabel = 'Archive All Classes in School'
  const checkSAML = async (id: any) => {
    try {
      const res = await getAPIRequest<{ error?: unknown } | undefined>(
        `/v1/sis/ingestion/saml?school_id=${id}`,
      )
      if (res && res.error == null) {
        setHasSAML(true)
      }
    } catch {}
  }

  const userCount = useAtomValue(schoolUserCountsWithDefaultAtom)

  useEffect(() => {
    if (id) return
    checkSAML(id)
  }, [id])

  if (!data) {
    return <div>Loading...</div>
  }

  const acsURL = `${apiUrl()}/v1/saml/acs?school_id=${id}`
  const metadataURL = `${apiUrl()}/v1/saml/metadata?school_id=${id}`

  const school = data
  return (
    <div className="content-color">
      {currentDialog}
      <SchoolHeader data={data} />
      {hasSAML && (
        <p>
          SAML Metadata Endpoint: <b>{metadataURL}</b>
        </p>
      )}
      {hasSAML && (
        <p>
          SAML Assertion Endpoint: <b>{acsURL}</b>
        </p>
      )}
      <hr />
      <div>
        <span>Searchable: </span>
        <span>{school.school_searchable}</span>
        <span className="section-buttons">
          <Button
            variant="outlined"
            color="secondary"
            onClick={updateSearchable}
            size="small"
          >
            Update
          </Button>
        </span>
      </div>
      <hr />
      <div>
        {school?.school_sis_id && (
          <SyncSISButton name={school.name} schoolID={Number(school.id)} />
        )}
        <div className="section-buttons">
          <CSVUpload schoolID={Number(school.id)} />
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/schools/${school.id}/edit`}
          >
            Edit School
          </Button>
          {school.deleted_at && (
            <ReinstateResourceButton
              name={school.name}
              resource="School"
              schoolID={school.id}
              districtID={school?.district?.id}
            />
          )}
        </div>
        <hr />
        <div className="section-buttons">
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/users?filter={"school":"${school.id}"}`}
            style={{ marginRight: '10px' }}
          >
            View School Users
          </Button>
          <span>
            Total Admins: {userCount.admin_count} <b>| </b>
          </span>
          <span>
            Total Teachers: {userCount.teacher_count} <b>| </b>
          </span>
          <span style={{ marginRight: '10px' }}>
            Total Students: {userCount.student_count}
          </span>
          <DeleteOrgUsersButton
            schoolID={school.id}
            name={school.name}
            resource="school"
          />
        </div>
      </div>
      <hr />
      <div className="section-buttons">
        <ArchiveClassButton
          id={school.id}
          level={'school'}
          archive={true}
          label={archiveButtonLabel}
        />
        {school.school_courses.length > 0 && (
          <>
            <span style={{ marginRight: '5px', marginLeft: '5px' }}>
              <b> | </b>
            </span>
            <DeleteDuplicateCurriculaButton schoolID={school.id} />
          </>
        )}
      </div>
      <hr />
      <Box
        sx={{ display: 'flex' }}
        width={'100%'}
        style={{ overflowY: 'auto', maxHeight: '300px' }}
      >
        <GetSchoolCurricula
          schoolId={school.id}
          bundledCurricula={school.school_courses}
          onDeleted={reFetchSchool}
        />
      </Box>
      <hr />
      <div>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            Curricula
          </AccordionSummary>
          <AccordionDetails>
            <Box width={'100%'}>
              <Box sx={{ display: 'flex', marginTop: '1%' }}>
                <AddCurriculum
                  schoolDistrictID={school.district?.id}
                  schoolID={school.id}
                  onAdded={reFetchSchool}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            Add new user to school
          </AccordionSummary>
          <AccordionDetails>
            <UserCreateWithSchool school_id={school.id} />
          </AccordionDetails>
        </Accordion>
        <div>
          <SchoolFeatures />
        </div>
      </div>
    </div>
  )
}
